import type { FocalEventPosterImageSource } from './FocalEventPosterImage';
import { RsvpVisibilitySelector } from './RsvpVisibilitySelector';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import type { RSVPVisibility } from '@/lib/api/apiTypes';
import { cn } from '@/lib/utils';
import {
  CalendarCheckIcon,
  CheckIcon,
  Clock3Icon,
  MapPinIcon,
  XIcon,
} from 'lucide-react-native';
import * as React from 'react';
import { Pressable, View } from 'react-native';

export type RsvpConfirmSheetProps = {
  title: string;
  posterURL?: string | null;
  posterImage?: FocalEventPosterImageSource | null;
  dayLabel?: string | null;
  distanceLabel?: string | null;
  timeLabel: string;
  timeDetail?: string | null;
  venueName: string;
  venueDetail?: string | null;
  visibility: RSVPVisibility;
  isBusy: boolean;
  isFull?: boolean;
  onChangeVisibility: (value: RSVPVisibility) => void;
  onConfirm: () => void;
  onClose: () => void;
};

export function RsvpConfirmSheet({
  title,
  distanceLabel,
  timeLabel,
  timeDetail,
  venueName,
  venueDetail,
  visibility,
  isBusy,
  isFull = false,
  onChangeVisibility,
  onConfirm,
  onClose,
}: RsvpConfirmSheetProps) {
  return (
    <View className="pb-3 pt-1">
      <View className="flex-row items-start gap-3">
        <View className="flex-1 gap-1">
          <View className="flex-row items-center gap-2">
            <View className="size-8 items-center justify-center rounded-full bg-primary/15">
              <Icon as={CalendarCheckIcon} size={15} className="text-primary" />
            </View>
            <Text variant="large" className="text-xl font-semibold text-foreground">
              {isFull ? 'Event full' : "Confirm you're going"}
            </Text>
          </View>
          <Text variant="small" className="text-muted-foreground" numberOfLines={2}>
            {title}
          </Text>
          {distanceLabel ? (
            <Text className="text-[12px] font-semibold text-primary">{distanceLabel}</Text>
          ) : null}
        </View>
      </View>

      <View className="mt-4 overflow-hidden rounded-[16px] border border-border bg-input/20">
        <CompactInfoRow icon={Clock3Icon} label={timeLabel} muted={timeDetail} />
        <CompactInfoRow icon={MapPinIcon} label={venueName} muted={venueDetail} isLast />
      </View>

      <View className="mt-3">
        <RsvpVisibilitySelector
          visibility={visibility}
          disabled={isBusy || isFull}
          onChangeVisibility={onChangeVisibility}
        />
      </View>

      <View className="mt-3 gap-2">
        <Pressable
          className={cn(
            'h-12 flex-row items-center justify-center gap-2 rounded-[18px]',
            isFull
              ? 'border border-border-strong bg-surface-raised'
              : 'bg-primary shadow-xl shadow-primary/20'
          )}
          disabled={isBusy || isFull}
          onPress={onConfirm}>
          {!isFull && !isBusy ? (
            <Icon as={CheckIcon} size={15} className="text-primary-foreground" />
          ) : null}
          <Text className={cn('font-semibold', isFull ? 'text-muted-foreground' : 'text-primary-foreground')}>
            {isFull ? 'Full' : isBusy ? 'Confirming...' : 'Confirm going'}
          </Text>
        </Pressable>
        <Pressable
          className="h-11 flex-row items-center justify-center gap-2 rounded-[16px]"
          onPress={onClose}>
          <Icon as={XIcon} size={14} className="text-muted-foreground" />
          <Text className="font-semibold text-muted-foreground">Not now</Text>
        </Pressable>
      </View>
    </View>
  );
}

function CompactInfoRow({
  icon,
  label,
  muted,
  isLast = false,
}: {
  icon: React.ComponentProps<typeof Icon>['as'];
  label: string;
  muted?: string | null;
  isLast?: boolean;
}) {
  return (
    <View className={cn('flex-row items-start gap-3 p-3', !isLast && 'border-b border-border')}>
      <View className="mt-0.5 size-7 items-center justify-center rounded-full bg-surface-raised">
        <Icon as={icon} size={14} className="text-muted-foreground" />
      </View>
      <View className="flex-1 gap-0.5">
        <Text variant="small" className="text-foreground">
          {label}
        </Text>
        {muted ? (
          <Text variant="small" className="text-muted-foreground" numberOfLines={1}>
            {muted}
          </Text>
        ) : null}
      </View>
    </View>
  );
}
